/**
 * WinderBulkActionBar — floating bulk-action bar for a winder selection.
 *
 * Same shape as CalibreBulkActionBar and the References listing's
 * ReferenceBulkActionBar (dark pill pinned to the bottom of the viewport, count
 * → divider → Clear → actions), so every listing behaves identically once rows
 * are ticked.
 *
 * Purely PRESENTATIONAL, like the calibre bar: every handler, modal and write
 * stays in CuratedWinders, which owns the selection.
 *
 * The action set is deliberately small. There is no "Set Make" here even though
 * the memo's sketch listed one: `make` feeds the doc id, so re-making a winder
 * in bulk would silently strand every `UserWinder.winderCatalogId` pointing at
 * it. Make changes belong in the editor, one at a time, where the id is pinned.
 */

export interface WinderBulkActionBarProps {
  /** Number of selected appliances. The caller renders the bar only when > 0. */
  count: number;
  /** A bulk write is in flight — disables every action. */
  busy?: boolean;
  onAiEnhance: () => void;
  onSetCountry: () => void;
  onSetPriceTier: () => void;
  onSetDeprecated: (deprecated: boolean) => void;
  onClearSelection: () => void;
}

export function WinderBulkActionBar({
  count,
  busy = false,
  onAiEnhance,
  onSetCountry,
  onSetPriceTier,
  onSetDeprecated,
  onClearSelection,
}: WinderBulkActionBarProps) {
  const disabled = busy || count === 0;

  return (
    <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-50'>
      <div className='flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl'>
        <span className='text-sm font-medium whitespace-nowrap'>
          {count.toLocaleString()} selected
        </span>
        <div className='h-4 w-px bg-gray-600' />
        <button
          onClick={onClearSelection}
          disabled={busy}
          className='text-gray-400 hover:text-white disabled:opacity-50 text-sm transition-colors'
        >
          Clear
        </button>
        <button
          onClick={onAiEnhance}
          disabled={disabled}
          title='Run AI enrichment over the selected appliances, then review and apply per-field'
          className='bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          AI Enhance
        </button>
        <button
          onClick={onSetCountry}
          disabled={disabled}
          title='Set the country of manufacture on every selected appliance that does not already have one'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set Country
        </button>
        <button
          onClick={onSetPriceTier}
          disabled={disabled}
          title='Set the price tier on every selected appliance that does not already have one'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set Price Tier
        </button>
        <button
          onClick={() => onSetDeprecated(true)}
          disabled={disabled}
          title='Hide the selected appliances from pickers. Existing user winders keep working.'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Deprecate
        </button>
        <button
          onClick={() => onSetDeprecated(false)}
          disabled={disabled}
          title='Return the selected appliances to the pickers'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Un-deprecate
        </button>
      </div>
    </div>
  );
}
